import { COMMON_TEXT } from "@/constants/text";
import {
  MRT_Table,
  useMantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table";
import { type ReactElement } from "react";

interface MantineGroupTableProps<
  TParent extends Record<string, any>,
  TChild extends Record<string, any>
> {
  data: TParent[];
  columns: MRT_ColumnDef<TParent>[];
  getSubRows: (row: TParent) => TChild[] | undefined;
  onRowClick?: (row: TParent) => void;
  maxHeight?: string | number;
}

const TABLE_BACKGROUND_COLOR = "#161B21";
const TABLE_BORDER = "1px solid white";
const HEADER_TEXT_COLOR = "gray";
const ROW_TEXT_COLOR = "#FFFFFF";

export function MantineGroupTable<
  TParent extends Record<string, any>,
  TChild extends Record<string, any>
>({
  data,
  columns,
  getSubRows,
  onRowClick,
  maxHeight,
  ...etcProps
}: MantineGroupTableProps<TParent, TChild>): ReactElement {
  const table = useMantineReactTable({
    columns,
    data,
    enableExpanding: true,
    getSubRows: (row) => getSubRows(row) as unknown as TParent[],
    mantineTableBodyRowProps: ({ row }) => {
      const isSubRow = row.parentId !== undefined; // Check if the row is a sub-row

      return {
        sx: {
          color: isSubRow ? "#40cdfc" : ROW_TEXT_COLOR,
          cursor: isSubRow ? "default" : "pointer",
        },
        onClick: () => {
          const rowData = row.original;
          if (!isSubRow && onRowClick) {
            onRowClick(rowData); // 상위 행만 클릭 가능하게
          }
        },
      };
    },
    localization: {
      noRecordsToDisplay: COMMON_TEXT.DATA_EMPTY.NO_DATA,
    },
    mantineTableHeadCellProps: {
      style: {
        background: TABLE_BACKGROUND_COLOR,
        color: HEADER_TEXT_COLOR,
      },
      sx: {
        border: `${TABLE_BORDER} !important`,
      },
    },
    mantineTableBodyCellProps: ({ cell, row }) => {
      const isSubRow = row.parentId !== undefined;

      const unitList = ["EA", "KG", "CM"];
      const cellValue = cell.getValue();
      const isUnit =
        typeof cellValue === "string" &&
        unitList.some((unit) =>
          cellValue?.toLowerCase().includes(unit.toLowerCase())
        );
      const numberValue = isUnit
        ? parseFloat(cellValue.replace(/[^\d.-]/g, ""))
        : NaN;
      const isNumber = !isNaN(numberValue);
      return {
        style: {
          fontSize: "0.7rem",
          textAlign: isSubRow ? "center" : isNumber ? "right" : "left",
          background: TABLE_BACKGROUND_COLOR,
        },
        sx: {
          border: `${TABLE_BORDER} !important`,
        },
      };
    },
    mantineTableProps: { style: { tableLayout: "fixed", width: "100%" } },
    // 가로로 너무 좁아질 때를 대비한 래퍼 스크롤(선택)
    // mantinePaperProps: { style: { overflowX: "auto" } },
    // mantineTableProps: {
    //   sx: {
    //     "tbody tr:nth-of-type(1) > td": {
    //       fontSize: "1.0rem",
    //       backgroundColor: TABLE_BACKGROUND_COLOR,
    //     },
    //   },
    // },
    mantineTableContainerProps: {
      sx: {
        maxHeight: maxHeight ?? "100%",
        overflowX: "auto",
        overflowY: "auto",
      },
    },
    enableStickyHeader: true,
    enablePagination: false,
    ...etcProps,
  });

  return <MRT_Table table={table} />;
}
